import {Copy, MapPin, MessageSquare, Share2} from "lucide-react";

import {Button} from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip";
import {toast} from "@/src/modules/core/hooks/use-toast";
import {cn} from "@/lib/utils";

import {
  copyHouseholdAddress,
  formatHouseholdShareText,
  getGoogleMapsShareUrl,
  getSmsShareUrl,
  getWhatsAppShareUrl,
  isNativeShareAvailable,
  shareHouseholdAddressNative,
} from "../lib/share-household-address";

type ResidentHomeShareAddressButtonProps = {
  residentName: string;
  address: string;
  className?: string;
};

export function ResidentHomeShareAddressButton({
  residentName,
  address,
  className,
}: ResidentHomeShareAddressButtonProps) {
  const shareText = formatHouseholdShareText(residentName, address);
  const canNativeShare = isNativeShareAvailable(shareText);

  const handleNativeShare = async () => {
    const result = await shareHouseholdAddressNative(shareText);
    if (result === "failed") {
      toast.error("Could not open share options");
    }
  };

  const handleCopy = async () => {
    const copied = await copyHouseholdAddress(shareText);
    if (copied) {
      toast.success("Address copied");
    } else {
      toast.error("Could not copy address");
    }
  };

  const openWhatsApp = () => {
    window.open(getWhatsAppShareUrl(shareText), "_blank", "noopener,noreferrer");
  };

  const openSms = () => {
    window.location.href = getSmsShareUrl(shareText);
  };

  const openGoogleMaps = () => {
    window.open(getGoogleMapsShareUrl(address), "_blank", "noopener,noreferrer");
  };

  return (
    <span className={cn("inline-flex align-middle", className)}>
      <DropdownMenu>
      <Tooltip>
        <TooltipTrigger
          render={
            <DropdownMenuTrigger
              render={
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground inline-flex size-7 shrink-0 p-0"
                  aria-label="Share household address"
                />
              }
            />
          }
        >
          <Share2 className="size-3.5" aria-hidden />
        </TooltipTrigger>
        <TooltipContent>Share address</TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end">
        {canNativeShare ? (
          <DropdownMenuItem onClick={() => void handleNativeShare()}>
            <Share2 className="size-4" />
            Share…
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem onClick={openWhatsApp}>
          <MessageSquare className="size-4" />
          WhatsApp
        </DropdownMenuItem>
        <DropdownMenuItem onClick={openSms}>
          <MessageSquare className="size-4" />
          Text message
        </DropdownMenuItem>
        <DropdownMenuItem onClick={openGoogleMaps}>
          <MapPin className="size-4" />
          Google Maps
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => void handleCopy()}>
          <Copy className="size-4" />
          Copy address
        </DropdownMenuItem>
      </DropdownMenuContent>
      </DropdownMenu>
    </span>
  );
}
